/**
 * Contact MailPhone Service
 *
 * Handles email and phone operations for contacts
 *
 * @module ContactMailPhoneService
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { APP_SCHEMA_ENDPOINTS, buildUrlWithParams } from '../api';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

/**
 * ContactMailPhone Read DTO
 */
export interface ContactMailPhoneRead {
  contact_mailphone_id: number;
  fk_contact_id: number;
  fk_category_id: number;
  contact_mailphone_data: string;
  is_primary?: boolean;
  lastupdated?: string;
  lastupdateduser?: string;
  ToCheck?: string | null; // Date when data needs to be checked (set by nightly script if data is missing)
}

/**
 * ContactMailPhone Update DTO
 */
export interface ContactMailPhoneUpdate {
  contact_mailphone_data?: string;
  fk_category_id?: number;
  is_primary?: boolean;
}

/**
 * ContactMailPhone Create DTO
 */
export interface ContactMailPhoneCreate {
  fk_contact_id: number;
  fk_category_id: number;
  contact_mailphone_data: string;
  is_primary?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ContactMailPhoneService {
  private http = inject(HttpClient);

  private createHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
    });
  }

  private handleError = (error: unknown): Observable<never> => {
    console.error('❌ ContactMailPhoneService Error:', error);
    throw error;
  };

  /**
   * Get all contact mailphone records by contact ID
   */
  getContactMailPhones(contactId: number): Observable<ContactMailPhoneRead[]> {
    const url = buildUrlWithParams(`${APP_SCHEMA_ENDPOINTS.contactMailPhone.list}`, {
      skip: 0,
      limit: 100,
    });

    return this.http
      .get<ContactMailPhoneRead[]>(url, { headers: this.createHeaders() })
      .pipe(catchError(this.handleError));
  }

  /**
   * Create a new contact mailphone entry
   */
  createContactMailPhone(data: ContactMailPhoneCreate): Observable<ContactMailPhoneRead> {
    return this.http
      .post<ContactMailPhoneRead>(APP_SCHEMA_ENDPOINTS.contactMailPhone.create, data, {
        headers: this.createHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  /**
   * Update an existing contact mailphone entry
   */
  updateContactMailPhone(
    mailphoneId: number,
    data: ContactMailPhoneUpdate
  ): Observable<ContactMailPhoneRead> {
    return this.http
      .put<ContactMailPhoneRead>(APP_SCHEMA_ENDPOINTS.contactMailPhone.update(mailphoneId), data, {
        headers: this.createHeaders(),
      })
      .pipe(catchError(this.handleError));
  }

  /**
   * Delete a contact mailphone entry
   */
  deleteContactMailPhone(mailphoneId: number): Observable<void> {
    return this.http
      .delete<void>(APP_SCHEMA_ENDPOINTS.contactMailPhone.delete(mailphoneId), {
        headers: this.createHeaders(),
      })
      .pipe(catchError(this.handleError));
  }
}
